import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { EposMenuItem } from '../../../types';
import AllergenList from './AllergenList';
import Modal, { MODAL_TRANSITION_MS } from '../../../Components/Modal';

/** A smaller card than the promo one - it only holds a few lines of text. */
const Card = styled.div`
  position: relative;
  width: min(100%, 520px);
  padding: 32px;
  border-radius: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposInfoCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-info-exit-active &,
  .epos-info-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposInfoCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.4px;
`;

const Description = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  opacity: 0.75;
`;

/** Hairline between the text and the allergens, so the list reads as its own block. */
const Divider = styled.div`
  height: 1px;
  background: var(--eposBorderColor);
`;

interface Props {
  item: EposMenuItem;
  open: boolean;
  onClose: () => void;
}

/**
 * What an upsell is: its description (skipped when empty) and allergens
 * ("Inga"/"None" when it has none). A tap anywhere outside the card closes it.
 */
const UpsellInfoModal: FC<Props> = ({ item, open, onClose }) => {
  return (
    <Modal
      open={open}
      exitPrefix="epos-info"
      onBackdropTap={onClose}
      testId="epos-upsell-info-modal">
      <Card>
        <Title>{item.title}</Title>
        {!!item.description && <Description>{item.description}</Description>}
        <Divider />
        <AllergenList tags={item.allergyTags} />
      </Card>
    </Modal>
  );
};

export default memo(UpsellInfoModal);
